import React, { useEffect, useState, useRef } from "react";
import BotService from "../services/botService";
import { downArrow, sendImg, savvyicon } from "../config/images";
import Markdown from "react-markdown";
import Selector from "./Selector";
import ReferralFormContainer from "./ReferralFormContainer";
import ReferralOptions from "./ReferralOptions";
import AdaptiveCard from "./AdaptiveCard";
import initializeChat from "./ChatInitializer";
import {
	Message,
	Attachment,
	Inputs,
	CustomChatBotProps,
	EventData,
} from "../types/chatbot.types";

const CustomChatBot: React.FC<CustomChatBotProps> = ({
	onFooterClick,
	initialloder,
}) => {
	// State declarations
	const [inputs, setInputs] = useState<Inputs>({});
	const [showInput, setShowInput] = useState<boolean>(true);
	const [initializedChat, setInitializedChat] = useState<boolean>(false);
	const [messages, setMessages] = useState<Message[]>([]);
	const [inputValue, setInputValue] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedOption, setSelectedOption] = useState<string | null>(null);
	const [rate, setRate] = useState<number>(0);
	const [buttons, setButtons] = useState<any[]>([]);
	const [showReferralOptions, setShowReferralOptions] = useState<boolean>(false);
	const [showReferralForm, setShowReferralForm] = useState<boolean>(false);
	const [eventdata, setEventdata] = useState<EventData[]>([]);
	const [selectedForm, setSelectedForm] = useState<EventData | null>(null);
	const [iframeActive, setIframeActive] = useState<boolean>(false);

	// Ref declarations
	const botServiceRef = useRef<BotService | null>(null);
	const chat = useRef<HTMLDivElement>(null);
	const socketRef = useRef<WebSocket | null>(null);

	useEffect(() => {
		if (!initializedChat) {
			initializeChat({
				setLoading,
				setInitializedChat,
				botServiceRef,
				openWebSocket,
			});
		}
	}, [initializedChat]);

	function openWebSocket(streamUrl: any) {
		const socket = new WebSocket(streamUrl);
		socketRef.current = socket;
		// Listen for messages
		socket.onopen = () => {
			console.log("WebSocket connection opened");
		};

		socket.onmessage = (event) => {
			if (!event.data) return;

			const data = JSON.parse(event.data);
			const message = data.activities[0];
			// Handle event type messages
			if (message.type === "event") {
				try {
					const eventValue =
						typeof message.value === "string"
							? JSON.parse(message.value)
							: message.value;

					setEventdata((prevEvents) => [...prevEvents, eventValue]);
				} catch (error) {
					console.error("Error parsing event data:", error);
					setEventdata((prevEvents) => [...prevEvents, message.value]);
				}
			}

			if (message.type === "message") {
				if (message.text === "form submitted") return;
				setMessages((prevMessages) => [...prevMessages, message]);
			}

			// if message has any suggested actions or attachments then hide the input field and only when its from.role is from the bot
			if (message.from.id === "bot-Savvy-1") {
				if (
					(message.attachments && message.attachments.length > 0) ||
					(message.suggestedActions &&
						message.suggestedActions.actions.length > 0)
				) {
					setShowInput(false);
				} else {
					setShowInput(true);
				}
			}

			//scroll to the bottom of the chat

			console.log('scrolling to the bottom');
			setTimeout(() => {
				chat.current?.scrollTo({ behavior: "smooth", top: chat.current.scrollHeight });
			}, 200);
		};

		// Handle errors
		socket.onerror = (error) => {
			console.error("WebSocket error:", error);
		};

		// Handle close event
		socket.onclose = () => {
			console.log("WebSocket connection closed");
		};
	}

	const handleSendMessage = async () => {
		if (inputValue.trim()) {
			const botService = botServiceRef.current;
			if (botService) {
				setLoading(true);
				await botService.sendMessage(inputValue);
				setLoading(false);
			}
			setInputValue("");
			setSelectedOption(null);
		}
	};

	const handleReferralClick = async (index: number, data: any) => {
		setShowReferralForm(true);
		setShowReferralOptions(false);
		setIframeActive(true);
		setSelectedForm(data);
	};

	const closeIframe = () => {
		setShowReferralForm(false);
		setIframeActive(false);
		setShowInput(true);
	};

	const handleOptionClick = async (button: any) => {
		setShowInput(false);
		setSelectedOption(button.title);
		setButtons([]);

		if (button.type === "openUrl") {
			window.open(button.value, "_blank");
			return;
		}

		setLoading(true);
		const botService = botServiceRef.current;
		if (botService) {
			await botService.sendMessage(button.title);
			setLoading(false);
		}
		setSelectedOption(null);
	};

	const renderMessage = (message: Message) => {
		if (message.attachments && message.attachments.length > 0) {
			return (
				<div key={message.id}>
					{message.attachments.map((attachment, index) =>
						renderAttachment(attachment, index)
					)}
				</div>
			);
		} else {
			return (
				<>
					<div
						className={`message ${message.from?.id === "bot-Savvy-1" ? "bot" : "user"
							}`}
						key={message.id}
					>
						<div className="_text">
							<Markdown>{message.text}</Markdown>
						</div>
					</div>
					{message.suggestedActions &&
						message.suggestedActions.actions.length > 0 && (
							<Selector
								options={message.suggestedActions.actions}
								onChange={handleOptionClick}
							/>
						)}
				</>
			);
		}
	};

	const renderAttachment = (attachment: Attachment, index: number) => {
		switch (attachment.contentType) {
			case "application/vnd.microsoft.card.hero":
				return renderHeroCard(attachment.content, index);
			case "application/vnd.microsoft.card.adaptive":
				return (
					<AdaptiveCard
						key={index}
						content={attachment.content}
						index={index}
						inputs={inputs}
						setInputs={setInputs}
						rate={rate}
						setRate={setRate}
						botServiceRef={botServiceRef}
						socketRef={socketRef}
						setLoading={setLoading}
						openWebSocket={openWebSocket}
					/>
				);
			default:
				return null;
		}
	};

	const renderHeroCard = (content: any, index: number) => {
		const ratingButtons = content.buttons.filter(
			(button: any) => button.type === "rating"
		);

		return (
			<div className="hero-card" key={index}>
				{content.images && content.images.length > 0 && (
					<img
						src={content.images[0].url}
						alt={content.title}
						className="hero-card-image"
					/>
				)}
				<h3 className="_textTitle">{content.title}</h3>
				<p className="_text">{content.text}</p>
				{ratingButtons.length > 0 && (
					<div className="hero-card-buttons ">
						{ratingButtons.map((button: any, idx: number) => (
							<button
								key={idx}
								className={
									button.title === inputValue
										? "hero-card-rating selected"
										: "hero-card-rating"
								}
								onClick={() => handleOptionClick(button)}
							>
								{button.title}
							</button>
						))}
					</div>
				)}
			</div>
		);
	};

	return (
		<div
			onClick={(e) => {
				onFooterClick();
				e.stopPropagation();
			}}
			className={`chat-container ${showReferralForm ? "iframe-visible" : ""}`}
		>
			<div onClick={(e) => e.stopPropagation()} className="sub-container">
				<div className="header">
					<div
						style={{
							display: "flex",
							alignItems: "center",
							flexDirection: "row",
							paddingLeft: "1rem",
							alignContent: "center",
							height: "3.75rem",
						}}
					>
						<img
							style={{ width: "2rem", height: "2rem", borderRadius: "50%" }}
							src={savvyicon}
							alt="Bot"
						/>
						<p className="title">{"Savvy"}</p>
					</div>
				</div>

				<div className="messages-wrapper">
					<div className="messages-container">
						<div ref={chat} className="messages">
							{initialloder ? (
								<div className="loading">
									<div className="loader"></div>
								</div>
							) : (
								<>
									{messages.map((message) => (
										<React.Fragment key={message.id}>
											{renderMessage(message)}
										</React.Fragment>
									))}
								</>
							)}
						</div>
					</div>

					{showReferralForm && (
						<ReferralFormContainer
							iframeActive={iframeActive}
							selectedForm={selectedForm}
							onClose={closeIframe}
						/>
					)}
				</div>

				{buttons.length > 0 && (
					<div className="hero-card-buttons leftAlign">
						{buttons.map((button: any, idx: number) => {
							if (button.type === "options") {
								return (
									<button
										key={idx}
										className={
											selectedOption === button.title
												? "hero-card-button selected"
												: "hero-card-button"
										}
										onClick={() => handleOptionClick(button)}
									>
										{button.title}
									</button>
								);
							} else if (button.type === "openUrl") {
								return (
									<button
										key={idx}
										className="hero-card-button"
										onClick={() => handleOptionClick(button)}
									>
										{button.title}
									</button>
								);
							} else {
								return null;
							}
						})}
					</div>
				)}

				<ReferralOptions
					eventdata={eventdata}
					showReferralOptions={showReferralOptions}
					showReferralForm={showReferralForm}
					onSelectClick={() => {
						setShowReferralOptions(true);
						setShowInput(true);
					}}
					onReferralItemClick={handleReferralClick}
				/>

				{showInput && (
					<div className="input-container">
						<>
							<input
								className="input"
								type="text"
								value={inputValue}
								placeholder=""
								onChange={(e) => setInputValue(e.target.value)}
								onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
							/>

							<button onClick={handleSendMessage} className="send-button">
								<img alt="send_icon" className="image" src={sendImg} />
							</button>
						</>
					</div>
				)}
				<div className="footer _downArrow">
					<button onClick={onFooterClick} className="footer-button">
						<img src={downArrow} alt="Bot" />
					</button>
				</div>
			</div>
		</div>
	);
};

export default CustomChatBot;
